import { autoinject, bindable, Container } from 'aurelia-framework';
import { saveAs } from 'file-saver';
import { PubSub } from 'lib/event/PubSub';
import { AsyncErrorHandler, ComponentLoadingFader } from 'lib/ui';
import { MooringFleetAnchorLine, MooringComponentType, UpdateMooringFleetAnchorLineDto, MooringReviewDto } from 'models/mooring';
import { ToastService } from 'services';
import { MooringOfferService, MooringService } from 'services/mooring';
import { OfferViewContext } from '../../OfferViewContext';
import { ConfirmDialog } from 'components/dialogs/confirm/confirm-dialog';
import { I18N } from 'aurelia-i18n';

@autoinject
export class FleetAnchorLinesContainer {
  @bindable
  protected mooringId: number;

  @bindable
  protected offerId: number;

  @bindable
  protected reviewId?: number;
  protected review: MooringReviewDto;

  protected context = OfferViewContext.useContext(this.container);

  protected controlView: boolean;

  protected anchorLines: MooringFleetAnchorLine[] = [];

  constructor(
    private container: Container,
    protected element: Element,
    private mooringService: MooringService,
    private toaster: ToastService,
    private pubsub: PubSub,
    private t: I18N,
    private confirmation: ConfirmDialog,
    private mooringOfferService: MooringOfferService
  ) {
    pubsub.sub('entity:updated', async () => {
      void this.getFleetAnchorLines();
    });

    pubsub.sub('list-entity:updated', async () => {
      void this.getFleetAnchorLines();
    });
  }

  protected toggleControlView() {
    this.controlView = !this.controlView;
  }

  @AsyncErrorHandler
  protected async exportFleetAnchorLines() {
    const blob = await this.mooringService.exportProductionFleetAnchorLines(this.mooringId);
    saveAs(blob.blob, blob.fileName);
  }

  @AsyncErrorHandler
  @ComponentLoadingFader
  async attached() {
    if (!this.mooringId) {
      throw new Error('mooringId is required => mooring-id.bind="<id>"');
    }

    await this.getFleetAnchorLines();
  }

  @AsyncErrorHandler
  protected async getFleetAnchorLines() {
    const { data } = await this.mooringService.getFleetAnchorLines(this.mooringId);
    const anchorLines = data;
    if (anchorLines.length) {
      for (const line of anchorLines) {
        line.Components?.sort((a, b) => a.SortIndex - b.SortIndex);
      }
      setTimeout(() => {
        this.anchorLines = anchorLines;
      }, 10);
    }

    this.review = this.context.getState().offerReview;
  }

  @AsyncErrorHandler
  protected async onSaveFleetAnchorLines({ entries, canNavigateOnSave }: { entries: MooringFleetAnchorLine[]; canNavigateOnSave: boolean }) {
    const dto = entries.map(
      (line) =>
        ({
          Id: line.Id,
          Slope: line.Slope,
          CornerNo: line.CornerNo,
          DimForce: line.DimForce,
          MoorlogNo: line.MoorlogNo,
          AnalysisNo: line.AnalysisNo,
          IsAccident: line.IsAccident,
          TotalLength: line.TotalLength ? line.TotalLength : 0,
          LengthOfRope: line.LengthOfRope ? line.LengthOfRope : 0,
          StopPositionLat: line.StopPositionLat,
          StartPositionLat: line.StartPositionLat,
          StopPositionLong: line.StopPositionLong,
          MblAnchoring: line.MblAnchoring,
          StartPositionLong: line.StartPositionLong,
          LengthOfBottomRope: line.LengthOfBottomRope ? line.LengthOfBottomRope : 0,
          MblRopes: line.MblRopes,
          MblSinkRopes: line.MblSinkRopes,
          MblStraps: line.MblStraps,
          BottomAnchoringUplift: line.BottomAnchoringUplift,
          DepthAtAnchoringPoint: line.DepthAtAnchoringPoint,
          HoldingPowerAnchoring: line.HoldingPowerAnchoring,
          MblChainShackleConnectionPlates: line.MblChainShackleConnectionPlates,
          MblUsedChainRopeWithKnot: line.MblUsedChainRopeWithKnot,
          Components: line.Components,
        }) satisfies UpdateMooringFleetAnchorLineDto
    );

    await this.mooringService.updateFleetAnchorLines(this.mooringId, dto);
    this.toaster.showUpdated();
    this.pubsub.publish('list-entity:updated', { name: MooringComponentType.anchorLine });
    if (canNavigateOnSave) {
      this.pubsub.publish('tabs:change-guard-ok', null);
    }
  }

  protected async setReviewStatus(reviewed: boolean) {
    const confirmed = await this.confirmation.confirmYesCancel(
      'dialog.areYouSure',
      reviewed
        ? this.t.tr('general.markAsCheckedConfirmationText', { x: this.t.tr('mooring.fleetAnchorLine_plural').toLowerCase() })
        : this.t.tr('general.markAsUnCheckedConfirmationText', { x: this.t.tr('mooring.fleetAnchorLine_plural').toLowerCase() })
    );

    if (!confirmed) return;

    await this.mooringOfferService.updateReviewStep(this.reviewId, { Step: 'FLEETANCHORLINES', Reviewed: reviewed });
    this.toaster.showSuccess(reviewed ? 'mooring.setAsChecked' : 'mooring.setAsNotChecked');
    this.pubsub.publish('entity-parent:updated', null);
  }

  protected unbind() {
    this.pubsub.unsub();
  }

  protected detached() {
    this.pubsub.unsub();
  }
}
